/**
 * Mock of `/api/media` for `VITE_MOCK=1` (the real route is
 * `apps/app/src/routes/media.ts`, the checks live in
 * `packages/core/src/media/`). It follows the same rules the server does:
 * the type is decided by looking at the bytes (never the file name or the
 * browser's `type`), at most 2 MB, editor+ to upload, any role to list, no
 * delete. Nothing is stored anywhere: an upload becomes a `data:` URL so the
 * preview can show it, which is why this must never be used outside mock mode.
 */
import type { MediaImage, Role } from '../../types/api';

export const MOCK_MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_BYTES = MOCK_MAX_IMAGE_BYTES;
const DAY = 24 * 60 * 60 * 1000;

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

export function swatch(color: string, label: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="300" viewBox="0 0 600 300">` +
    `<rect width="600" height="300" fill="${color}"/>` +
    `<text x="300" y="165" font-family="sans-serif" font-size="36" fill="#fff" text-anchor="middle">${label}</text></svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

// Demo images only (shown as SVG data URLs in mock mode; the real platform never stores SVG).
const store: MediaImage[] = [
  ['img-seed-3', '#5457D6', 'Summer banner', 'summer-banner.png', 3],
  ['img-seed-2', '#0F766E', 'Product shot', 'product-shot.jpg', 9],
  ['img-seed-1', '#B45309', 'Header', 'header.webp', 21],
].map(([id, color, label, name, daysAgo]) => ({
  id: id as string,
  url: swatch(color as string, label as string),
  contentType: 'image/png' as const,
  width: 600,
  height: 300,
  byteSize: 48_000,
  name: name as string,
  createdAt: Date.now() - (daysAgo as number) * DAY,
}));
let nextId = 100;

export interface Sniffed {
  contentType: MediaImage['contentType'];
  width: number;
  height: number;
}

/** Signature check on the bytes. Sizes are read for PNG and GIF; the others show 0 (the real server reads all four). */
export function sniff(b: Uint8Array): Sniffed | null {
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const ascii = (from: number, n: number) => String.fromCharCode(...b.subarray(from, from + n));
  if (b.length >= 24 && b[0] === 0x89 && ascii(1, 3) === 'PNG') {
    return { contentType: 'image/png', width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (b.length >= 10 && (ascii(0, 6) === 'GIF87a' || ascii(0, 6) === 'GIF89a')) {
    return {
      contentType: 'image/gif',
      width: view.getUint16(6, true),
      height: view.getUint16(8, true),
    };
  }
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
    return { contentType: 'image/jpeg', width: 0, height: 0 };
  }
  if (b.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
    return { contentType: 'image/webp', width: 0, height: 0 };
  }
  return null;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/** Adds an image to the demo library (used by the mock "copy all" so copied images show up in the image picker). */
export function addMockLibraryImage(image: Omit<MediaImage, 'id' | 'createdAt'>): MediaImage {
  const added: MediaImage = { ...image, id: `img-${nextId++}`, createdAt: Date.now() };
  store.unshift(added);
  return added;
}

/** Returns a response for `/media` requests, or null for any other path. */
export async function handleMockMedia(
  method: string,
  pathname: string,
  params: URLSearchParams,
  init: RequestInit,
  role: Role,
): Promise<Response | null> {
  if (pathname !== '/media') return null;

  if (method === 'GET') {
    const limit = Math.max(1, Number(params.get('limit')) || 60);
    const before = params.get('before');
    const sorted = [...store].sort((a, b) => b.createdAt - a.createdAt || (a.id < b.id ? 1 : -1));
    const start = before ? sorted.findIndex((i) => i.id === before) + 1 : 0;
    const page = sorted.slice(start, start + limit);
    const more = start + limit < sorted.length;
    return json({ images: page, nextBefore: more ? (page[page.length - 1]?.id ?? null) : null });
  }

  if (method === 'POST') {
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    const file = init.body instanceof FormData ? init.body.get('file') : null;
    if (!(file instanceof File)) return json({ error: 'invalid_request' }, 400);
    if (file.size === 0) return json({ error: 'empty', message: 'The file is empty.' }, 400);
    if (file.size > MAX_BYTES) {
      return json({ error: 'too_large', message: 'The image is larger than 2 MB.' }, 413);
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    const found = sniff(bytes);
    if (!found) {
      return json(
        { error: 'unsupported_type', message: 'Only JPEG, PNG, GIF and WebP images are accepted.' },
        415,
      );
    }
    const image: MediaImage = {
      id: `img-${nextId++}`,
      url: `data:${found.contentType};base64,${toBase64(bytes)}`,
      contentType: found.contentType,
      width: found.width,
      height: found.height,
      byteSize: bytes.length,
      name: file.name || null,
      createdAt: Date.now(),
    };
    store.unshift(image);
    return json({ image }, 201);
  }

  return null;
}
